// Canvas renderer for the hero journey halftone cascade (slash-dot field).

import {
  HERO_JOURNEY_CASCADE_GLYPHS,
  type HeroJourneyCascadeGlyph,
} from '@/components/support-mainnet/hero-journey-cascade-glyphs';
import {
  applyHeroJourneyCascadeSpotlight,
  getHeroJourneyCascadeSpotlightInfluence,
  type HeroJourneyCascadeSpotlightTrail,
} from '@/components/support-mainnet/hero-journey-cascade-spotlight';

export const HERO_JOURNEY_CASCADE_CONFIG = {
  viewW: 1920,
  viewH: 1080,
  r: 4.14,
  slash: 3.51,
  lineW: 1.44,
  angleDeg: 145,
  wavelength: 520,
  speed: 0.8,
  lo: 0.42,
  hi: 1.9,
  introMs: 4200,
  shades: ['#9c9c9c', '#6e6e6e'] as const,
} as const;

export type PreparedHeroJourneyCascadeGlyph = {
  x: number;
  y: number;
  color: string;
  baseOpacity: number;
  proj: number;
  projNorm: number;
};

export type HeroJourneyCascadeFit = {
  dpr: number;
  scale: number;
  ox: number;
  oy: number;
  width: number;
  height: number;
};

const TWO_PI: number = Math.PI * 2;

export function prepareHeroJourneyCascadeGlyphs(
  glyphs: readonly HeroJourneyCascadeGlyph[] = HERO_JOURNEY_CASCADE_GLYPHS,
): PreparedHeroJourneyCascadeGlyph[] {
  const angleRad = (HERO_JOURNEY_CASCADE_CONFIG.angleDeg * Math.PI) / 180;
  const dx = Math.cos(angleRad);
  const dy = Math.sin(angleRad);

  let projMin = Number.POSITIVE_INFINITY;
  let projMax = Number.NEGATIVE_INFINITY;

  for (const glyph of glyphs) {
    const proj = glyph[0] * dx + glyph[1] * dy;
    if (proj < projMin) {
      projMin = proj;
    }
    if (proj > projMax) {
      projMax = proj;
    }
  }

  const projSpan = projMax - projMin || 1;

  return glyphs.map((glyph) => {
    const proj = glyph[0] * dx + glyph[1] * dy;
    return {
      x: glyph[0],
      y: glyph[1],
      color: HERO_JOURNEY_CASCADE_CONFIG.shades[glyph[2]],
      baseOpacity: glyph[3],
      proj,
      projNorm: (proj - projMin) / projSpan,
    };
  });
}

export function computeHeroJourneyCascadeFit(
  width: number,
  height: number,
  devicePixelRatio: number,
): HeroJourneyCascadeFit {
  const dpr = Math.min(devicePixelRatio || 1, 2);
  const scale = Math.min(
    width / HERO_JOURNEY_CASCADE_CONFIG.viewW,
    height / HERO_JOURNEY_CASCADE_CONFIG.viewH,
  );
  const ox = (width - HERO_JOURNEY_CASCADE_CONFIG.viewW * scale) / 2;
  const oy = (height - HERO_JOURNEY_CASCADE_CONFIG.viewH * scale) / 2;

  return { dpr, scale, ox, oy, width, height };
}

export function drawHeroJourneyCascadeFrame(
  ctx: CanvasRenderingContext2D,
  glyphs: readonly PreparedHeroJourneyCascadeGlyph[],
  fit: HeroJourneyCascadeFit,
  timeMs: number,
  introProgress: number,
  spotlight?: HeroJourneyCascadeSpotlightTrail | null,
): void {
  const { dpr, scale, ox, oy, width, height } = fit;
  const t = timeMs / 1000;
  const kSpace = TWO_PI / HERO_JOURNEY_CASCADE_CONFIG.wavelength;
  const kTime = TWO_PI * HERO_JOURNEY_CASCADE_CONFIG.speed;
  const introP = introProgress;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width * dpr, height * dpr);
  ctx.save();
  ctx.translate(ox, oy);
  ctx.scale(scale, scale);
  ctx.lineWidth = HERO_JOURNEY_CASCADE_CONFIG.lineW;
  ctx.lineCap = 'round';

  for (const glyph of glyphs) {
    const phase = kTime * t - glyph.proj * kSpace;
    const wave = 0.5 + 0.5 * Math.sin(phase);
    let mult =
      HERO_JOURNEY_CASCADE_CONFIG.lo +
      (HERO_JOURNEY_CASCADE_CONFIG.hi - HERO_JOURNEY_CASCADE_CONFIG.lo) * wave;

    if (introP < 1) {
      const front = introP * 1.25;
      const edge = 0.14;
      let reveal = Math.min(1, Math.max(0, (front - glyph.projNorm) / edge));
      reveal = reveal * reveal * (3 - 2 * reveal);
      mult *= reveal;
    }

    let alpha = glyph.baseOpacity * mult;
    if (alpha <= 0.004) {
      continue;
    }
    if (alpha > 1) {
      alpha = 1;
    }

    let strokeColor = glyph.color;
    let radius: number = HERO_JOURNEY_CASCADE_CONFIG.r;
    let slash: number = HERO_JOURNEY_CASCADE_CONFIG.slash;

    if (spotlight !== null && spotlight !== undefined && spotlight.samples.length > 0) {
      const influence = getHeroJourneyCascadeSpotlightInfluence(
        spotlight,
        glyph.x,
        glyph.y,
        fit.scale,
      );
      if (influence > 0) {
        const lit = applyHeroJourneyCascadeSpotlight(strokeColor, alpha, radius, slash, influence);
        strokeColor = lit.color;
        alpha = lit.alpha;
        radius = lit.radius;
        slash = lit.slash;
      }
    }

    ctx.globalAlpha = alpha;
    ctx.strokeStyle = strokeColor;
    const { x, y } = glyph;

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, TWO_PI);
    ctx.moveTo(x - slash, y + slash);
    ctx.lineTo(x + slash, y - slash);
    ctx.stroke();
  }

  ctx.restore();
}

export function getHeroJourneyCascadeIntroProgress(elapsedMs: number): number {
  if (HERO_JOURNEY_CASCADE_CONFIG.introMs <= 0) {
    return 1;
  }
  return Math.min(1, elapsedMs / HERO_JOURNEY_CASCADE_CONFIG.introMs);
}
